import "zod/compile";
import { z } from "zod";

/**
 * Lifecycle metadata for a feature flag. The registry in `feature-flags.ts`
 * only type-checks against this (via `satisfies`); the Zod parse, the owner
 * roster lookup and the date horizons run in CI (`erp-scripts flags check`),
 * never at import time, because the browser bundle imports the registry.
 *
 * See docs/platform/feature-flag-lifecycle.md.
 */

/** Calendar date, UTC, `YYYY-MM-DD`. Compared as strings: lexicographic order is chronological. */
export const IsoDateSchema = z.iso.date();

export const FLAG_KINDS = [
  /** Gates a feature while it rolls out. Temporary: must ramp, then be deleted. */
  "release",
  /** A/B or multivariate test. Temporary: ends with a decision (`rampBy`), then removal. */
  "experiment",
  /** Kill switch meant to live indefinitely. Needs a periodic review, not removal. Never pages. */
  "kill-switch",
  /** Dev/test scaffolding (e.g. `test-flag`). Never reported. */
  "internal",
] as const;
export type FlagKind = (typeof FLAG_KINDS)[number];

/**
 * Whose identity the flag is evaluated against: a signed-in user, or the
 * service identity (`isSystemFeatureEnabled`). Derives `SYSTEM_FEATURE_FLAGS`.
 */
export const FLAG_EVALUATION_SCOPES = ["user", "system"] as const;

/**
 * Where the flag is evaluated and enforced. Vocabulary mirrors PostHog's
 * per-flag `evaluation_runtime` ("server" | "client" | "all"):
 *   - `server`: backend only (`backend/src`),
 *   - `client`: UI only (`frontend/src`); the flag hides or shows UI and the
 *     backend does not enforce it,
 *   - `all`: both.
 * CI derives the actual value from call sites and fails on drift. This is a
 * label, not an access control: a `client` flag is not a security boundary.
 */
export const FLAG_EVALUATION_RUNTIMES = ["server", "client", "all"] as const;
export type FlagEvaluationRuntime = (typeof FLAG_EVALUATION_RUNTIMES)[number];

/** GitHub handle; CI checks it exists in `.github/github-slack-users.json`. */
export const FlagOwnerSchema = z.string().regex(/^[A-Za-z0-9-]+$/);

const baseShape = {
  description: z.string().trim().min(10),
  owner: FlagOwnerSchema,
  /** Optional product/domain label (ap, oms, tasks, platform…). The hook for team routing later. */
  area: z
    .string()
    .regex(/^[a-z0-9-]+$/)
    .optional(),
  ticket: z.url().optional(),
  scopes: z.array(z.enum(FLAG_EVALUATION_SCOPES)).min(1).readonly().optional(),
  evaluationRuntime: z.enum(FLAG_EVALUATION_RUNTIMES),
  /** Day the flag was added. Anchors the lifetime horizons for new flags. */
  createdOn: IsoDateSchema,
};

const temporaryShape = {
  ...baseShape,
  /** Fully rolled out (or, for an experiment, decided) by this date. */
  rampBy: IsoDateSchema,
  /** Flag and its dead branch deleted from code by this date. Past it, the daily Slack report @-mentions the owner. */
  removeBy: IsoDateSchema,
};

export const FlagDefinitionSchema = z
  .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("release"), ...temporaryShape }),
    z.strictObject({ kind: z.literal("experiment"), ...temporaryShape }),
    z.strictObject({
      kind: z.literal("kill-switch"),
      ...baseShape,
      /** Re-justify the switch by this date. Past it: a warning, never a page. */
      reviewBy: IsoDateSchema,
    }),
    z.strictObject({ kind: z.literal("internal"), ...baseShape }),
  ])
  .superRefine((def, ctx) => {
    if ("rampBy" in def) {
      if (def.rampBy > def.removeBy) {
        ctx.addIssue({
          code: "custom",
          message: "rampBy must be on or before removeBy",
          path: ["rampBy"],
        });
      }
      if (def.rampBy < def.createdOn) {
        ctx.addIssue({
          code: "custom",
          message: "rampBy must be on or after createdOn",
          path: ["rampBy"],
        });
      }
    }
    if ("reviewBy" in def && def.reviewBy < def.createdOn) {
      ctx.addIssue({
        code: "custom",
        message: "reviewBy must be on or after createdOn",
        path: ["reviewBy"],
      });
    }
    if (def.scopes?.includes("system") && def.evaluationRuntime === "client") {
      ctx.addIssue({
        code: "custom",
        message:
          'a "system" scope flag is evaluated by the backend, so evaluationRuntime cannot be "client"',
        path: ["evaluationRuntime"],
      });
    }
  });

export type FlagDefinition = z.input<typeof FlagDefinitionSchema>;
